const numberFormat = require("number-format.js");

function capitalize(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "Sem Nome";
}

function NumberToHumanRange(n, numberOfFixed, numberToSlice = 3) {
  var ranges = [
    { divider: 1e18, suffix: "E" },
    { divider: 1e15, suffix: "P" },
    { divider: 1e12, suffix: "T" },
    { divider: 1e9, suffix: "B" },
    { divider: 1e6, suffix: "M" },
    { divider: 1e3, suffix: "K" },
  ];

  for (var i = 0; i < ranges.length; i++) {
    if (n >= ranges[i].divider) {
      const numberDivided = Number(n / ranges[i].divider);
      const normalizedNumber =
        numberDivided % 1 === 0
          ? numberDivided.toFixed(0)
          : numberDivided.toFixed(numberOfFixed);
      return (
        normalizedNumber.toString().slice(0, numberToSlice) + ranges[i].suffix
      );
    }
  }
  return Number(n)
    .toFixed(0)
    .toString()
    .slice(0, numberToSlice);
}

function isEmpty(obj) {
  for (var x in obj) {
    return false;
  }
  return true;
}

function isDate(val) {
  return toString.call(val) === "[object Date]";
}

function formatDate(val) {
  if (!val) return "-";
  if (isDate(val))
    return Intl.DateTimeFormat("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(val);
  return Intl.DateTimeFormat("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(val));
}

function formatNumber(val) {
  if (val === 0) return val;

  return val % 1 === 0
    ? numberFormat("###.###,##", val)
    : numberFormat("###.###,#0", val);
}

// Wallets of this type carry a monetary `value` alongside `points` on every
// cart item (see the `wallet.wallet_type` nested under each cart item in the
// carts API response). Other wallet types keep the points-only display.
const CAMPAIGN_OPTION_WALLET_TYPE = "Voucher::CampaignOption";

// The wallet type isn't a cart-level field — it's nested on each item
// (`item.wallet.wallet_type`) — so it's read off the first item that carries
// it, since every item in a given cart shares the same active wallet.
function getCartWalletType(cart) {
  if (!cart || !cart.items) return null;

  const itemWithWallet = cart.items.find(
    (item) => item.wallet && item.wallet.wallet_type,
  );
  return itemWithWallet ? itemWithWallet.wallet.wallet_type : null;
}


function cartIsAllowed(cart) {
  let allowed = true;
  let toRemove = []
  
  if (!cart || !cart.items || !cart.items.length) return  { allowed, toRemove };

  // The most recently added product is the "priority item" — the API
  // appends new items to the end of `cart.items`, so it's the last entry.
  // Compatibility is judged from its perspective against everything
  // already in the cart:
  //  1. A digital card (is_a_card === true) must be the only item in the
  //     cart — every other item, card or physical, is incompatible.
  //  2. A physical product is only incompatible with digital cards; it
  //     remains compatible with other physical products.
  //
  // "toRemove" collects the names of offending items shown to the user.
  const priorityItem = cart.items[cart.items.length - 1];
  const otherItems = cart.items.slice(0, -1);
  const isPriorityCard = priorityItem.product.is_a_card === true;

  if (isPriorityCard) {
    allowed = false
    otherItems.forEach((item) => toRemove.push(item.product.name));
  } 
  else {
    const conflictingCards = otherItems.filter(
      (item) => item.product.is_a_card === true,
    );
    if (conflictingCards.length > 0) {
      allowed = false;
      conflictingCards.forEach((item) => toRemove.push(item.product.name));
    }
  }
  
  return { allowed, toRemove };
}

function getCartWalletIsFixedType(cart) {
  if (!cart || !cart.items) return null;
  const itemWithWallet = cart.items.find(
    (item) => item.wallet && item.wallet.wallet_type,
  );
  return itemWithWallet &&
    itemWithWallet.wallet &&
    itemWithWallet.wallet.campaign_options &&
    itemWithWallet.wallet.campaign_options.options 
    ? itemWithWallet.wallet.campaign_options.options.has_fixed_value
    : null;
}

function getCartTotalValue(cart) {
  if (!cart || !cart.items) return 0;

  return cart.items.reduce((sum, item) => sum + (item.value || 0), 0);
}

export {
  isEmpty,
  capitalize,
  isDate,
  formatNumber,
  formatDate,
  NumberToHumanRange,
  CAMPAIGN_OPTION_WALLET_TYPE,
  getCartWalletType,
  getCartTotalValue,
  getCartWalletIsFixedType,
  cartIsAllowed,
};

export function withSlug({ slug }, path) {
  return `/${slug}${path}`;
}

export function substring(target, limit, suffix) {
  return target.length > limit
    ? [target.substring(0, limit - 1), suffix].join("")
    : target;
}

export function eventPath(evt) {
  const path = (evt.composedPath && evt.composedPath()) || evt.path,
    target = evt.target;

  if (path != null) {
    // Safari doesn't include Window, but it should.
    return path.indexOf(window) < 0 ? path.concat(window) : path;
  }

  if (target === window) {
    return [window];
  }

  function getParents(node, memo) {
    memo = memo || [];
    var parentNode = node.parentNode;

    if (!parentNode) {
      return memo;
    } else {
      return getParents(parentNode, memo.concat(parentNode));
    }
  }

  return [target].concat(getParents(target), window);
}

// Named entities covered by the regex fallback below (no DOM available).
// Keeps tag delimiters plus the accented/Latin-special letters (e.g.
// "&eacute;" -> "é") that show up in Portuguese copy.
const HTML_ENTITY_MAP = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  aacute: "á", Aacute: "Á",
  agrave: "à", Agrave: "À",
  acirc: "â", Acirc: "Â",
  atilde: "ã", Atilde: "Ã",
  auml: "ä", Auml: "Ä",
  eacute: "é", Eacute: "É",
  egrave: "è", Egrave: "È",
  ecirc: "ê", Ecirc: "Ê",
  euml: "ë", Euml: "Ë",
  iacute: "í", Iacute: "Í",
  igrave: "ì", Igrave: "Ì",
  icirc: "î", Icirc: "Î",
  iuml: "ï", Iuml: "Ï",
  oacute: "ó", Oacute: "Ó",
  ograve: "ò", Ograve: "Ò",
  ocirc: "ô", Ocirc: "Ô",
  otilde: "õ", Otilde: "Õ",
  ouml: "ö", Ouml: "Ö",
  uacute: "ú", Uacute: "Ú",
  ugrave: "ù", Ugrave: "Ù",
  ucirc: "û", Ucirc: "Û",
  uuml: "ü", Uuml: "Ü",
  ccedil: "ç", Ccedil: "Ç",
  ntilde: "ñ", Ntilde: "Ñ",
};

// Decodes HTML entities (tag delimiters like "&lt;div&gt;" as well as named
// Latin-special characters like "&eacute;" -> "é" and numeric refs like
// "&#233;"/"&#xE9;") so the result can be safely handed to
// `dangerouslySetInnerHTML` and parsed as real markup/text instead of
// showing up as literal escaped code.
export function parseHTMLTags(msg) {
  if (!msg) return msg;

  // When a DOM is available (browser, or Jest's jsdom environment) let the
  // browser's own parser do the decoding: it covers every named/numeric
  // HTML entity generically, not just the handful listed below.
  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = msg;
    return textarea.value;
  }

  // Fallback for non-DOM environments (e.g. Node 10 without jsdom): decode
  // tag delimiters, the common named entities, and numeric character refs
  // via regex, since `String.prototype.replaceAll` (ES2021) isn't available.
  return msg
    .replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (match, code) => {
      if (code[0] === "#") {
        const codePoint =
          code[1] === "x" || code[1] === "X"
            ? parseInt(code.slice(2), 16)
            : parseInt(code.slice(1), 10);
        return isNaN(codePoint) ? match : String.fromCharCode(codePoint);
      }
      return HTML_ENTITY_MAP[code] || match;
    });
}
